import { inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  ResolveFn,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { Store } from "@ngrx/store";
import { filter, map } from "rxjs";
import { InvalidOperationException } from "src/app/shared/exceptions/exceptions";
import { allFunnelHeaderMilestones } from "src/app/shared/routes/global-route.constant";
import { QueryStringParameterKey } from "src/app/shared/routes/shared/shared-route.constant";
import { RouteDataService } from "src/app/shared/services/route-data.service";
import { RouterService } from "src/app/shared/services/router.service";
import { AppState } from "src/app/shared/store/app.state";
import { selectFeatureState } from "src/app/shared/store/quote/term-life-insurance/quote.selectors";

// NOTE: Guards always trigger before a resolver triggers.
// Therefore our guard operates on the state / response of the submit from the previous page
// before a resolver has a chance to do something. Just to keep in mind.
export function createSkipDigitalApplicationPagesGuard(
  getDataResolver: ResolveFn<null>,
) {
  return (
    activatedRouteSnapshot: ActivatedRouteSnapshot,
    routerStateSnapshot: RouterStateSnapshot,
  ) => {
    const router = inject(Router);
    const store = inject(Store<AppState>);
    const routeDataService = inject(RouteDataService);
    const routerService = inject(RouterService);

    const quoteId = activatedRouteSnapshot.queryParamMap.get(
      QueryStringParameterKey.QUOTE_ID,
    );

    if (quoteId) {
      /**
       * The aim is to have a product agnostic {@link createSkipDigitalApplicationPagesGuard} guard.
       * Unfortunately we also require product specific data to be loaded (usually done via resolver).
       * This is why we allow passing in a resolver from the outside which is being used to fetch data from the backend.
       *
       * This aims to:
       * ---------------
       * => Load the data identically as it would have been loaded by only applying the resolver
       * => Make it transparent to someone looking at our routes that this guard is loading some data
       *
       * This comes at the price of prematurely triggering our resolver during the guard phase (which I don't think should cause any issues as long as the resolver sticks to only loading data)
       */

      void getDataResolver(activatedRouteSnapshot, routerStateSnapshot);

      const navigationRouteData = routeDataService.getRequiredNavigationData(
        activatedRouteSnapshot,
      );

      const nextNavigationPage = navigationRouteData.nextNavigationPage;
      const previousNavigationPage = navigationRouteData.previousNavigationPage;

      const allSteps = allFunnelHeaderMilestones.flatMap(
        (milestone) => milestone.steps,
      );

      const nextRouteIndex = allSteps.findIndex((step) =>
        // state.url shows the url a user would navigate to
        routerStateSnapshot.url.includes(step.path),
      );

      const initialRouteIndex = allSteps.findIndex((step) =>
        // router.url shows the url of the initial route
        router.url.includes(step.path),
      );

      // throws an error if someone would try to use that guard on pages without either next or back button (e.g. first or last page)
      // also throws an error if number of steps is too low to skip
      if (
        nextNavigationPage === undefined ||
        previousNavigationPage === undefined ||
        allSteps.length < 3
      ) {
        throw new InvalidOperationException(
          "This error occurs when the number of steps is lower than 3 or the guard is used on routes without either next or back button",
        );
      }

      return store.select(selectFeatureState).pipe(
        filter((featureState) => featureState.isBusy === false),
        map((featureState) => {
          // only happens if the user inserts a guarded url directly in the browser
          // or does a page refresh on a guarded page
          if (initialRouteIndex === -1) {
            // NOTE As a result the user can never directly navigate to the underwriting questions
            // by typing a url directly in the browser in case he has not passed them.
            return featureState.isLegacyApplication ? false : true;
          }
          if (featureState.isLegacyApplication) {
            // checks if a user navigates to next page (clicks next button)
            if (nextRouteIndex > initialRouteIndex) {
              void routerService.navigateToPage(nextNavigationPage);
            }

            // checks if a user navigates to previous page (clicks back button)
            if (nextRouteIndex < initialRouteIndex) {
              void routerService.navigateToPage(previousNavigationPage);
            }
          }
          return true;
        }),
      );
    }
    return true;
  };
}
